import { useEffect, useState } from "react";
import axios from "axios";
import { Heart, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SupportersStrip — two-tab section ("Friends of the Phoenix" = person, "Supporters" = company).
 * Renders as a 3-up grid on desktop (md+), a 1-up swipeable on mobile.
 * Auto-advances every 8s, pauses on hover/focus. Click tracks via /api/supporters/{id}/click.
 *
 * Props:
 *   placement — filter rendered cards by placement, e.g. "homepage" or "my_corner".
 *   variant   — "section" (default — full padded section block) | "page" (no outer padding/title).
 */
export default function SupportersStrip({ placement, variant = "section" }) {
  const [tab, setTab] = useState("person"); // person | company
  const [people, setPeople] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [a, b] = await Promise.all([
          axios.get(`${API}/supporters?placement=${encodeURIComponent(placement)}&kind=person`),
          axios.get(`${API}/supporters?placement=${encodeURIComponent(placement)}&kind=company`),
        ]);
        if (!cancelled) {
          setPeople(a.data?.supporters || []);
          setCompanies(b.data?.supporters || []);
        }
      } catch {
        if (!cancelled) {
          setPeople([]);
          setCompanies([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [placement]);

  // Auto-flip to the populated tab if the current one is empty.
  // (Effect — never call setState during render.)
  useEffect(() => {
    if (loading) return;
    if (tab === "person" && people.length === 0 && companies.length > 0) {
      setTab("company");
    } else if (tab === "company" && companies.length === 0 && people.length > 0) {
      setTab("person");
    }
  }, [loading, tab, people.length, companies.length]);

  if (loading) return null;
  // Hide the whole section if both tabs are empty
  if (people.length === 0 && companies.length === 0) return null;

  const list = tab === "person" ? people : companies;

  const Wrapper = ({ children }) =>
    variant === "section" ? (
      <section
        data-testid={`supporters-strip-${placement}`}
        className="py-12 md:py-16 bg-[#0A0A0A]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {children}
        </div>
      </section>
    ) : (
      <div data-testid={`supporters-strip-${placement}`}>{children}</div>
    );

  return (
    <Wrapper>
      {variant === "section" && (
        <div className="flex items-baseline justify-between gap-3 mb-5 flex-wrap">
          <div className="flex items-center gap-3">
            <Heart size={16} className="text-[#DC2626]" />
            <h3 className="font-display text-2xl md:text-3xl text-white">
              {tab === "person" ? "Friends of the Phoenix" : "Supporters"}
            </h3>
          </div>
          <Link
            to="/supporters"
            data-testid={`supporters-strip-${placement}-see-all`}
            className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] hover:underline font-bold"
          >
            See All →
          </Link>
        </div>
      )}

      {/* Tab switcher */}
      <div className="flex gap-2 mb-5">
        <TabBtn
          active={tab === "person"}
          onClick={() => setTab("person")}
          disabled={people.length === 0}
          testId={`supporters-tab-person-${placement}`}
        >
          🐦 Friends · {people.length}
        </TabBtn>
        <TabBtn
          active={tab === "company"}
          onClick={() => setTab("company")}
          disabled={companies.length === 0}
          testId={`supporters-tab-company-${placement}`}
        >
          🤝 Supporters · {companies.length}
        </TabBtn>
      </div>

      <SupportersGrid list={list} placement={placement} kind={tab} />
    </Wrapper>
  );
}

function TabBtn({ active, disabled, onClick, testId, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={
        "px-3 py-2 text-[10px] uppercase tracking-widest font-bold border-2 transition-colors " +
        (active
          ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
          : disabled
            ? "border-zinc-900 text-neutral-700 cursor-not-allowed"
            : "border-zinc-800 text-neutral-400 hover:border-zinc-600 hover:text-white")
      }
    >
      {children}
    </button>
  );
}

function SupportersGrid({ list, placement, kind }) {
  const [carIdx, setCarIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  // Mobile carousel only auto-advances when there are 2+ items
  useEffect(() => {
    if (list.length <= 1 || paused) return;
    const t = setInterval(() => setCarIdx((i) => (i + 1) % list.length), 8000);
    return () => clearInterval(t);
  }, [list.length, paused]);

  useEffect(() => {
    if (carIdx >= list.length) setCarIdx(0);
  }, [list.length, carIdx]);

  if (list.length === 0) return null;

  async function handleClick(s) {
    try {
      const { data } = await axios.post(`${API}/supporters/${s.supporter_id}/click`);
      if (data?.url) window.open(data.url, "_blank", "noopener,noreferrer");
    } catch {
      // Silent — no-link supporters intentionally return url=null
    }
  }

  return (
    <>
      {/* Desktop: 3-up grid */}
      <div className="hidden md:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((s) => (
          <SupporterCard key={s.supporter_id} s={s} onClick={() => handleClick(s)} />
        ))}
      </div>

      {/* Mobile: single-card carousel */}
      <div
        className="md:hidden relative"
        onTouchStart={() => setPaused(true)}
        onTouchEnd={() => setPaused(false)}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <div key={list[carIdx].supporter_id} className="animate-affiliate-fade">
          <SupporterCard
            s={list[carIdx]}
            onClick={() => handleClick(list[carIdx])}
          />
        </div>
        {list.length > 1 && (
          <>
            <button
              type="button"
              aria-label="Previous"
              onClick={() => setCarIdx((i) => (i - 1 + list.length) % list.length)}
              data-testid={`supporters-prev-${placement}-${kind}`}
              className="absolute top-1/2 -translate-y-1/2 -left-2 w-9 h-9 bg-black/80 border border-[#D4AF37]/60 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black flex items-center justify-center"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              aria-label="Next"
              onClick={() => setCarIdx((i) => (i + 1) % list.length)}
              data-testid={`supporters-next-${placement}-${kind}`}
              className="absolute top-1/2 -translate-y-1/2 -right-2 w-9 h-9 bg-black/80 border border-[#D4AF37]/60 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black flex items-center justify-center"
            >
              <ChevronRight size={18} />
            </button>
            <div className="flex items-center justify-center gap-2 mt-3">
              {list.map((p, i) => (
                <button
                  key={p.supporter_id}
                  type="button"
                  aria-label={`Show supporter ${i + 1}`}
                  onClick={() => setCarIdx(i)}
                  className={
                    "h-1.5 transition-all " +
                    (i === carIdx ? "w-8 bg-[#D4AF37]" : "w-3 bg-zinc-700 hover:bg-zinc-500")
                  }
                />
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
}

export function SupporterCard({ s, onClick }) {
  const isPerson = s.kind === "person";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`supporter-card-${s.supporter_id}`}
      className="w-full text-left bg-[#0F0F0F] border-2 border-zinc-800 hover:border-[#D4AF37] transition-colors p-4 flex gap-3 items-start group"
    >
      {/* Avatar/logo */}
      <div
        className={
          "flex-shrink-0 w-16 h-16 bg-black border border-zinc-900 overflow-hidden " +
          (isPerson ? "rounded-full" : "")
        }
      >
        {s.image_url ? (
          <img
            src={s.image_url}
            alt={s.name}
            className="w-full h-full object-contain bg-black group-hover:scale-105 transition-transform"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center font-display text-xl text-[#D4AF37]">
            {(s.name || "?").charAt(0).toUpperCase()}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
          <span className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold truncate">
            {s.category || (isPerson ? "Friend" : "Supporter")}
          </span>
          {s.has_link && (
            <ExternalLink size={10} className="text-neutral-500" />
          )}
        </div>
        <div className="font-display text-base md:text-lg text-white leading-tight truncate">
          {s.name}
        </div>
        {s.tagline && (
          <div className="text-xs text-neutral-400 mt-0.5 line-clamp-2">
            {s.tagline}
          </div>
        )}
        {s.note && (
          <div className="text-[11px] text-neutral-500 italic mt-1.5 line-clamp-2">
            "{s.note}"
          </div>
        )}
      </div>
    </button>
  );
}
